import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { CustomerInfo, QuoteItem } from './types'

export function generateQuotePDF(customerInfo: CustomerInfo, quoteItems: QuoteItem[]) {
  const doc = new jsPDF();
  
  // Enhanced color palette with proper tuple types
  const colors = {
    primary: [81, 139, 196] as [number, number, number],
    accent: [124, 20, 20] as [number, number, number],
    darkBlue: [7, 36, 64] as [number, number, number],
    lightGray: [245, 247, 250] as [number, number, number],
    mediumGray: [150, 150, 150] as [number, number, number],
    darkGray: [50, 50, 50] as [number, number, number],
    white: [255, 255, 255] as [number, number, number]
  };

  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;

  // Add decorative header background
  doc.setFillColor(...colors.darkGray);
  doc.rect(0, 0, pageWidth, 35, 'F');
  
  // Add subtle bottom accent
  doc.setFillColor(...colors.lightGray);
  doc.rect(0, 35, pageWidth, 3, 'F');

  // Add logo with transparency support
  const logo = '/logo.png';
  try {
    // Load image with proper format and transparency alias
    const img = new Image();
    img.src = logo;
    doc.addImage(logo, 'PNG', 16, 10, 45, 15, undefined, 'FAST');
  } catch (e) {
    console.warn('Logo failed to load:', e);
  }

  // Header title with shadow effect
  doc.setFontSize(26);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colors.white);
  doc.text("QUOTE REQUEST", pageWidth - 16, 20, { align: 'right' });
  
  // Date with icon-like styling
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(255, 255, 255);
  doc.text(`Generated: ${new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })}`, pageWidth - 16, 28, { align: 'right' });

  // Customer Information Section with card-like design
  const cardY = 48;
  
  // Card background
  doc.setFillColor(...colors.lightGray);
  doc.roundedRect(14, cardY, pageWidth - 28, 42, 2, 2, 'F');
  
  // Section header with accent line
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colors.accent);
  doc.text("Customer Information", 20, cardY + 8);
  
  // Accent line under header
  doc.setDrawColor(...colors.accent);
  doc.setLineWidth(0.5);
  doc.line(20, cardY + 10, 70, cardY + 10);

  // Customer details in two columns
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...colors.darkGray);
  
  const leftCol = 20;
  const rightCol = 110;
  let detailY = cardY + 18;
  
  // Left column
  doc.setFont('helvetica', 'bold');
  doc.text("Company:", leftCol, detailY);
  doc.setFont('helvetica', 'normal');
  doc.text(customerInfo.company, leftCol + 22, detailY);
  
  detailY += 7;
  doc.setFont('helvetica', 'bold');
  doc.text("Contact:", leftCol, detailY);
  doc.setFont('helvetica', 'normal');
  doc.text(`${customerInfo.firstName} ${customerInfo.lastName || ''}`, leftCol + 22, detailY);
  
  // Right column
  detailY = cardY + 18;
  doc.setFont('helvetica', 'bold');
  doc.text("Email:", rightCol, detailY);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...colors.primary);
  doc.text(customerInfo.email, rightCol + 18, detailY);
  
  detailY += 7;
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colors.darkGray);
  doc.text("Phone:", rightCol, detailY);
  doc.setFont('helvetica', 'normal');
  doc.text(customerInfo.phone || 'N/A', rightCol + 18, detailY);

  // Items section header
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colors.accent);
  doc.text("Requested Items", 14, 102);
  
  doc.setDrawColor(...colors.accent);
  doc.setLineWidth(0.5);
  doc.line(14, 104, 64, 104);

  // Prepare table data
  const tableColumn = ["Part Number", "Manufacturer", "Description", "Qty"];
  const tableRows: (string | number)[][] = quoteItems.map(item => [
    item.partNumber,
    item.manufacturer,
    item.description,
    item.quantity
  ]);

  // Enhanced table with better styling
  autoTable(doc, {
    startY: 110,
    head: [tableColumn],
    body: tableRows,
    theme: 'striped',
    headStyles: {
      fillColor: colors.darkBlue,
      textColor: colors.white,
      fontStyle: 'bold',
      fontSize: 10,
      halign: 'left',
      cellPadding: 5,
    },
    columnStyles: {
      0: { cellWidth: 40, fontStyle: 'bold' },
      1: { cellWidth: 38 },
      2: { cellWidth: 80 },
      3: { cellWidth: 20, halign: 'center' },
    },
    alternateRowStyles: {
      fillColor: colors.lightGray,
    },
    styles: {
      font: 'helvetica',
      fontSize: 9,
      cellPadding: 4,
      lineColor: [220, 220, 220],
      lineWidth: 0.1,
      textColor: colors.darkGray,
    },
    margin: { left: 14, right: 14 },
    didDrawPage: (data) => {
      // Add footer to each page
      addPageFooter(doc, colors, pageWidth, pageHeight);
    },
  });

  // Add footer to first page if table doesn't trigger didDrawPage
  const finalY = (doc as any).lastAutoTable.finalY;
  if (finalY < pageHeight - 40) {
    addPageFooter(doc, colors, pageWidth, pageHeight);
  }

  // Save with descriptive filename
  const timestamp = new Date().toISOString().split('T')[0];
  doc.save(`ProSoft_Quote_${customerInfo.company.replace(/\s+/g, '_')}_${timestamp}.pdf`);
}

function addPageFooter(
  doc: jsPDF, 
  colors: { 
    darkBlue: [number, number, number]; 
    white: [number, number, number] 
  }, 
  pageWidth: number, 
  pageHeight: number
) {
  const pageCount = (doc as any).internal.getNumberOfPages();
  const currentPage = (doc as any).internal.getCurrentPageInfo().pageNumber;
  
  // Footer background
  doc.setFillColor(...colors.darkBlue);
  doc.rect(0, pageHeight - 20, pageWidth, 20, 'F');
  
  // Footer text
  doc.setFontSize(9);
  doc.setTextColor(...colors.white);
  doc.setFont('helvetica', 'normal');
  doc.text("ProSoft Electronics", 14, pageHeight - 11);
  
  // Page numbers
  doc.text(
    `Page ${currentPage} of ${pageCount}`,
    pageWidth - 14,
    pageHeight - 11,
    { align: 'right' }
  );
  
  // Subtle divider line
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(0.2);
  doc.line(14, pageHeight - 20, pageWidth - 14, pageHeight - 20);
}